import { useMemo, useState } from "react";
import { RefreshCw, Search, UserMinus } from "lucide-react";
import { DsBadge, DsButton, DsIcon, DsInput } from "../../../design-system";

/**
 * Users tab: every active employee with their assigned role label.
 * Default system roles show as Employee / Self or Reporting Manager;
 * custom roles show the custom role name. "Remove custom access" resets to EMPLOYEE.
 */
export default function RoleUsersView({
  users,
  loading,
  error,
  onRetry,
  canRemoveCustom,
  onRemoveCustom,
  busyUserId,
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | custom | default

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (Array.isArray(users) ? users : []).filter((u) => {
      if (filter === "custom" && !u.isCustomRole) return false;
      if (filter === "default" && !u.isDefaultRole) return false;
      if (!q) return true;
      return (
        String(u.name || "").toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q) ||
        String(u.empCode || "").toLowerCase().includes(q) ||
        String(u.roleName || "").toLowerCase().includes(q) ||
        String(u.roleCode || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, filter]);

  const customCount = (users || []).filter((u) => u.isCustomRole).length;
  const defaultCount = (users || []).length - customCount;

  return (
    <section
      aria-label="Users and role assignments"
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
    >
      <div className="shrink-0 space-y-2.5 border-b border-[hsl(var(--ds-border))] p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Users</h3>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              {customCount} custom · {defaultCount} default · {users?.length || 0} total
            </p>
          </div>
          <div className="inline-flex rounded-full border border-[hsl(var(--ds-border))] p-0.5">
            {[
              { id: "all", label: "All" },
              { id: "custom", label: "Custom role" },
              { id: "default", label: "Default role" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilter(opt.id)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                  filter === opt.id
                    ? "border border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]"
                    : "border border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="relative">
          <DsIcon
            icon={Search}
            size="sm"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
          />
          <DsInput
            type="search"
            placeholder="Search name, email, code or role"
            aria-label="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {error ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="ds-body text-[hsl(var(--ds-danger))]">{error}</p>
          <DsButton size="sm" variant="outline" onClick={onRetry}>
            <DsIcon icon={RefreshCw} size="sm" />
            Retry
          </DsButton>
        </div>
      ) : loading ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Loading users…</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
            {search || filter !== "all" ? "No users match this filter." : "No active employees found."}
          </p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-muted))]/80 backdrop-blur">
              <tr className="border-b border-[hsl(var(--ds-border))] text-[11px] uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                <th className="px-3 py-2.5 font-semibold">Employee</th>
                <th className="px-3 py-2.5 font-semibold">Code</th>
                <th className="px-3 py-2.5 font-semibold">Assigned role</th>
                <th className="px-3 py-2.5 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <tr
                  key={u.userId}
                  className="border-b border-[hsl(var(--ds-border))] last:border-0 hover:bg-[hsl(var(--ds-muted))]/40"
                >
                  <td className="px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-[hsl(var(--ds-fg))]">{u.name}</p>
                      <p className="truncate text-[11px] text-[hsl(var(--ds-muted-fg))]">{u.email}</p>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-[hsl(var(--ds-muted-fg))]">
                    {u.empCode || "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium text-[hsl(var(--ds-fg))]">{u.roleName}</span>
                      <DsBadge tone={u.isCustomRole ? "primary" : "neutral"}>
                        {u.isCustomRole ? "Custom" : "Default"}
                      </DsBadge>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {u.isCustomRole ? (
                      <DsButton
                        size="sm"
                        variant="outline"
                        disabled={!canRemoveCustom || busyUserId === u.userId}
                        title={
                          !canRemoveCustom
                            ? "Only an admin can remove custom role access."
                            : "Reset this user to the default Employee / Self role"
                        }
                        onClick={() => onRemoveCustom(u)}
                      >
                        <DsIcon icon={UserMinus} size="sm" />
                        {busyUserId === u.userId ? "Removing…" : "Remove custom access"}
                      </DsButton>
                    ) : (
                      <span className="text-[11px] text-[hsl(var(--ds-muted-fg))]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
